// Data-quality family: GET /api/v1/quality-metrics (Sentinel's quality service), sweep 2026-10-13 (/meters called the
// legacy route and got a 404).
//
// view=meters (the Meters page's "Data Quality" filter) <- ONE generic NomOS question per quantity: readings of every
// counted meter in a trailing window, grouped by meter (`Meter` scope, `site_id` = the meter, as the consumption
// breakdown asks). A counted meter (active, top-level, counted in the portfolio, on an active building) with
// no reading in the window carries the fact `meters_without_readings`. Production meters report `kwh_export`, so a
// meter with either quantity has readings.
// Difference from Sentinel, named in the definition and the notes: Sentinel judges each data source against its own
// reading period plus the provider's publication delay (an hourly meter is overdue after a few days); NomOS keeps no
// per-source reading period, so the window is one fixed span that also holds a monthly manual reading
// (QUIET_DAYS = one and a half monthly periods). An hourly meter that stopped a week ago is not listed yet.
// GAP: view=countries / buildings / fix_list (coverage shares, estimated shares, legal-entity and responsible facts,
//      double counts, superseded meters) have no NomOS producer: a visible NomosGapError.
import type { QualityMeter, QualityMetricDefinition, QualityMetricsParams, QualityMetricsResponse } from '@/types/quality';
import { queryRecords, refs, runQuery, scalar, type NomosFilter } from '../client';
import type { AnyFn, NomosRegistry } from '../registry';
import { localDate, localMidnight, tenantZone } from '../tz';
import { NomosGapError } from './fieldops';
import { countsInPeriod, METER_WINDOW_FIELDS } from './meterWindow';

type Row = Record<string, unknown>;

/** The trailing window a counted meter must have a reading in: one and a half monthly reading periods. */
export const QUIET_DAYS = 45;
export const METER_FACT = 'meters_without_readings';
const METERS_MAX = 2000;
const DAY = 86_400_000;

export const METER_FACT_DEFINITION: QualityMetricDefinition = {
  key: METER_FACT, label: 'Meter without readings', unit: 'flag', kind: 'binary', level: 'building', severity: 'error',
  definition: `A counted meter (active, physical, top-level, counted in the portfolio, on an active building) with no `
    + `reading in the last ${QUIET_DAYS} days - one and a half monthly reading periods, so a monthly manual meter is `
    + 'not listed before its next value is overdue.',
  why: 'Its consumption is missing from every total and review.',
  fix: "Re-link or re-sync the meter's data source, or enter the missing months.",
  field: 'Meters › meter (data source)',
};

const str = (x: unknown): string | null => { const s = scalar(x); return typeof s === 'string' && s !== '' ? s : null; };
const ref = (x: unknown): string | null => refs(x)[0]?.id ?? null;
const ymd = (d: Date) => d.toISOString().slice(0, 10);

/** Every record of `type` (pages of 1000). */
async function allRecords(type: string, fields: string[], filter?: NomosFilter) {
  const out: Array<{ id: string; v: Row }> = [];
  for (let offset = 0; ; offset += 1000) {
    const res = await queryRecords(type, { fields, filter, limit: 1000, cursor: offset });
    out.push(...res.rows.map((r) => ({ id: r.record_id, v: r.values })));
    if (res.rows.length === 0 || out.length >= res.total) break;
  }
  return out;
}

/** The meters with at least one value of `quantity` in the window: one grouped question, every page. */
async function metersWithReadings(quantity: string, period_start: string, period_end: string): Promise<Set<string>> {
  const out = new Set<string>();
  let cursor: string | undefined;
  for (let guard = 0; guard < 50; guard++) {
    const res = await runQuery<{ rows: Row[]; meta: Row }>({ op: 'reduce',
      scope: { entity_def: 'Meter', quantity, period_start, period_end, filter: { attribute: 'parent_meter', op: 'is_not_set', value: null } },
      reducer: { kind: 'group-by', agg: 'sum', dimensions: ['site_id'] }, page: { size: 1000, ...(cursor ? { cursor } : {}) } });
    for (const r of res.data?.rows ?? []) if (r.value !== null && r.value !== undefined) out.add(String(r.site_id));
    cursor = ((res.data?.meta?.page as Row | undefined)?.cursor as string | null | undefined) ?? undefined;
    if (!cursor) break;
  }
  return out;
}

async function meterQuality(params: QualityMetricsParams, now: Date): Promise<QualityMetricsResponse> {
  const zone = await tenantZone();
  const today = localDate(now, zone);
  const start = ymd(new Date(Date.parse(`${today}T00:00:00Z`) - QUIET_DAYS * DAY));
  const end = ymd(new Date(Date.parse(`${today}T00:00:00Z`) + DAY));
  const [ps, pe] = [localMidnight(start, zone), localMidnight(end, zone)];
  const [meters, sites, kwh, exported] = await Promise.all([
    allRecords('meter', ['name', 'site', 'is_active', ...METER_WINDOW_FIELDS, 'sentinel_meter_id'],
      { attribute: 'parent_meter', op: 'is_not_set', value: null }),
    allRecords('site', ['is_active', 'sentinel_building_id']),
    metersWithReadings('kwh', ps, pe),
    metersWithReadings('kwh_export', ps, pe).catch(() => new Set<string>()),   // a portfolio without production meters
  ]);
  const site = new Map(sites.map((s) => [s.id, s.v]));
  const counted = meters.filter((m) => {
    const s = site.get(ref(m.v.site) ?? '');
    // a meter counts while its active window overlaps the checked days (owner ruling 2026-10-20: no separate flag)
    return s && scalar(s.is_active) !== false && scalar(m.v.is_active) !== false && countsInPeriod(m.v, start, today);
  });
  const quiet: QualityMeter[] = counted.filter((m) => !kwh.has(m.id) && !exported.has(m.id)).map((m) => {
    const s = ref(m.v.site) ?? '';
    return { meter_id: str(m.v.sentinel_meter_id) ?? m.id, record_id: m.id, meter_name: str(m.v.name) ?? 'Meter',
      building_id: str(site.get(s)?.sentinel_building_id) ?? s, facts: [{ key: METER_FACT, detail: `no reading since ${start}` }] };
  }).sort((a, b) => a.meter_name.localeCompare(b.meter_name));
  const rows = params.gap && params.gap !== METER_FACT ? [] : quiet;
  const limit = Math.min(params.limit ?? METERS_MAX, METERS_MAX);
  const offset = params.cursor ? Number(params.cursor) || 0 : 0;
  return {
    answer: `${counted.length} meters checked: ${quiet.length} with no reading in the last ${QUIET_DAYS} days.`,
    period_start: start, period_end: today, totals: null, countries: null, buildings: null, fix_items: null,
    meters: rows.slice(offset, offset + limit), total: rows.length,
    next_cursor: offset + limit < rows.length ? String(offset + limit) : null,
    definitions: params.include_definitions ? [METER_FACT_DEFINITION] : null,
    export_urls: {},
    notes: [`A meter is listed when it has no reading in the last ${QUIET_DAYS} days. Sentinel judged each data source `
      + "against its own reading period and the provider's publication delay; NomOS keeps no per-source period, so an "
      + 'hourly meter that stopped recently is not listed yet.'],
  };
}

async function getQualityMetrics(params: QualityMetricsParams = {}): Promise<QualityMetricsResponse> {
  if ((params.view ?? 'countries') === 'meters') return meterQuality(params, new Date());
  throw new NomosGapError(`quality-metrics view=${params.view ?? 'countries'}: Sentinel's quality service (coverage and `
    + 'estimated shares, legal-entity and responsible facts, double counts, superseded meters) has no NomOS producer; '
    + 'only view=meters (meters without readings) is served');
}

export const qualityImpl: NomosRegistry = {
  getQualityMetrics: getQualityMetrics as unknown as AnyFn,
};
